import { StorageManager } from 'src/app/common/storage-manager.class';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    FilteredValue,
    ReactiveComputedFieldContext,
    ReactiveDropdownCatalogs,
} from '@fitogest/reactive-form-builder/interfaces';
import { FertirrigacionListsDefinition } from './list-variables';
import { FertirrigacionEnvironmentDefinition } from '../../environment-variables';
import { FertirrigacionModel } from 'src/app/models/tareas/fertirrigacion.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import {
  createFincaScopedLegacyFilter,
  createParcelasScopedLegacyFilter,
} from '../../../shared/tareas-location-dropdown.datasource';

export class FertirrigacionFormDefinition {
  public formType!: FormType;

  public env: FertirrigacionEnvironmentDefinition =
    new FertirrigacionEnvironmentDefinition();

  public lists: FertirrigacionListsDefinition =
    new FertirrigacionListsDefinition();

  public formFields!: Form<FertirrigacionModel>;

  public fieldNames: FormField<any>[] = [];

  public fieldLabels: string[] = [];

  public lastFieldEdited = '';

  public tag = '';

  public requestType: FormType = FormType.CREATION;

  public cliente = StorageManager.getClient();

  public usuario = StorageManager.getUser();

  public model: FertirrigacionModel = {
    id_cliente: +(this.cliente?.id ?? 0),
    id_usuario: +StorageManager.getUser().id,
  };

  public openDialog = false;

  public getFormFields(): FormField<any>[] {
    const formFields: FormField<any>[] = [
      {
        name: this.formType < 3 ? 'id_finca' : 'finca',
        label: this.env.fincaName,
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.fincas,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (it: FilteredValue<FincasModel>): boolean => {
              if (
                !this.env.hasClients ||
                this.formType === 2 ||
                !this.env.userCanSeeClientes
              ) {
                return true;
              } else if (Boolean(StorageManager.getClient())) {
                return String(it.value?.id_cliente ?? '') === String(StorageManager.getClient()?.id ?? '');
              }
              return true;
            },
          },
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: true,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasFincas,
        },
        order: 1,
      },
      {
        name: this.formType < 3 ? 'ids_sectores' : 'sectores_nombres',
        dependencies: [
          {
            source: 'id_finca',
            target: this.formType < 3 ? 'ids_sectores' : 'sectores_nombres',
            kind: 'filter',
            legacy: true,
          },
        ],
        dependencyBehaviour: {
          keepCurrentValueIfStillAvailable: true,
          keepValueUntilOptionsLoaded: true,
          preservePanelOnReload: true,
          preserveFilterOnReload: true,
        },
        label: this.env.sectoresName,
        input: {
          type: InputType.MULTISELECT_SEARCH,
          isEditable: true,
          data: this.lists.sectores,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: createFincaScopedLegacyFilter<FertirrigacionModel>(),
          },
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showSectores,
        },
        order: 2,
      },
      {
        name: this.formType < 3 ? 'ids_parcelas' : 'parcelas_nombres',
        dependencies: [
          {
            source: 'id_finca',
            target: this.formType < 3 ? 'ids_parcelas' : 'parcelas_nombres',
            kind: 'filter',
            legacy: true,
          },
          {
            source: 'ids_sectores',
            target: this.formType < 3 ? 'ids_parcelas' : 'parcelas_nombres',
            kind: 'filter',
            legacy: true,
          },
        ],
        dependencyBehaviour: {
          keepCurrentValueIfStillAvailable: true,
          keepValueUntilOptionsLoaded: true,
          preservePanelOnReload: true,
          preserveFilterOnReload: true,
        },
        label: this.env.parcelasName,
        input: {
          type: InputType.MULTISELECT_SEARCH,
          isEditable: true,
          data: this.lists.parcelas,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: createParcelasScopedLegacyFilter<FertirrigacionModel>({
              sectorFieldName: 'ids_sectores',
            }),
          },
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired:
            this.env.appName !== 'pyf' && this.env.appName !== 'donjardin',
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 3,
      },
      {
        name: 'fecha',
        label: 'Fecha',
        input: { type: InputType.CALENDAR, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 4,
      },
      {
        name: 'sistema_riego',
        label: 'Sistema de Riego',
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.sistemasRiego,
          dropdownConfiguration: {
            valuePrimaryKey: 'codigo_SIEX',
          },
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 5,
      },
      {
        name: 'volumen_riego',
        label: 'Volumen (m³/ha/año)',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 6,
      },
      {
        name: 'n_agua',
        label: 'N nítrico del agua (mg/l)',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 7,
      },
      {
        name: 'p205',
        label: 'P205 soluble en agua (mg/l)',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 8,
      },
      {
        name: 'aplicacion_residuos_r10',
        label: 'Aplicación de residuos R10',
        input: { type: InputType.NUMERIC, isEditable: true },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 10,
      },
      {
        name: 'documento_aplicacion',
        label: 'Documento de Aplicación',
        input: {
          type: InputType.SWITCH,
          isEditable: true,
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 11,
      },
      {
        name: 'id_comunidad_regantes',
        label: 'Comunidad de Regantes',
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.comunidadesRegantes,
          dropdownConfiguration: {
            valuePrimaryKey: 'id'
          }
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 12,
        buttons: [
          {
            class: 'btn btn-success border rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center',
            style: `
              width: 26px;
              height: 26px;
              margin-left: 6px;
              font-size: .95rem;
              transform: translateY(-0.5rem);
            `,
            icon: 'fa fa-plus',
            label: '',
            tooltip: 'Añadir nuevo valor',
            tooltipPosition: 'top',
            function: () => this.openDialog = true
          }
        ],
      },
      {
        name: 'observaciones',
        label: 'Observaciones',
        input: {
          type: InputType.TEXTAREA,
          isEditable: true,
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 13,
      },
    ];

    this.fieldNames = this.fieldNames.concat(
      formFields.filter((it) => it.conditions.isVisible)
    );
    this.fieldLabels = this.fieldLabels.concat(
      formFields.filter((it) => it.conditions.isVisible).map((it) => it.label)
    );
    return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getFormSections(): FormSection<FertirrigacionModel>[] {
    const sections: FormSection<FertirrigacionModel>[] = [
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getFormFields(),
        order: 1,
      },
      {
        name: '',
        width: 6,
        isVisible: true,
        fields: this.getHiddenFields(),
        order: 2,
      },
    ];

    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getHiddenFields(): FormField<any>[] {
    const hiddenFields: FormField<any>[] = [
      {
        name: 'id',
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.model.id,
          isEditable: false,
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 1,
      },
      {
        name: 'id_cliente',
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.resolveClientId(this.model.id_finca),
          isEditable: false,
        },
        computed: {
          dependsOn: ['id_finca'],
          resolve: (context: ReactiveComputedFieldContext<FertirrigacionModel>) => this.resolveClientId(
            context.getValue('id_finca'),
          ),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasClients && this.env.userCanSeeClientes,
        },
        order: 2,
      },
      {
        name: 'id_usuario',
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.getCurrentUser(this.model.id_finca),
          isEditable: false,
        },
        computed: {
          dependsOn: ['id_finca'],
          resolve: (context: ReactiveComputedFieldContext<FertirrigacionModel>) => this.getCurrentUser(
            context.getValue('id_finca'),
          ),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 3,
      },
      {
        name: 'cultivo',
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        computed: {
          dependsOn: ['ids_parcelas'],
          resolve: (context: ReactiveComputedFieldContext<FertirrigacionModel>) => this.resolveParcelasJoinedField(
            context.getValue('ids_parcelas'),
            'cultivo',
          ),
        },
        order: 4,
      },
      {
        name: 'variedad',
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        computed: {
          dependsOn: ['ids_parcelas'],
          resolve: (context: ReactiveComputedFieldContext<FertirrigacionModel>) => this.resolveParcelasJoinedField(
            context.getValue('ids_parcelas'),
            'variedad',
          ),
        },
        order: 5,
      },
      // aportacion_teorica_nitrogeno: calculado en PHP (GET de fertirrigacion),
      // ya no se envía desde Angular al guardar.
    ];

    return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private resolveParcelasJoinedField(value: unknown, property: keyof ParcelasModel): string {
    const parcelas = Array.isArray(value)
      ? value as ParcelasModel[]
      : Array.isArray(this.model.ids_parcelas)
        ? this.model.ids_parcelas as unknown as ParcelasModel[]
        : [];

    return parcelas
      .map((parcela: ParcelasModel) => String(parcela?.[property] ?? ''))
      .filter((fieldValue: string) => fieldValue.length > 0)
      .join(';');
  }

  private resolveClientId(fincaValue: unknown): number | string | undefined {
    if (Boolean(this.cliente?.id)) {
      return this.cliente?.id;
    }

    return this.resolveSelectedFinca(fincaValue)?.id_cliente ?? undefined;
  }

  private getCurrentUser(fincaValue: unknown): string {
    const currentUserId =
      this.formType !== FormType.EDITION ? this.usuario?.id ?? '' : '';
    const selectedFinca = this.resolveSelectedFinca(fincaValue);
    const fincaAssignedUserId = selectedFinca ? selectedFinca.id_usuario : '';
    const userId = this.env.showTareasMto ? fincaAssignedUserId : currentUserId;

    return userId?.toString() ?? '';
  }

  private resolveSelectedFinca(value: unknown): FincasModel | null {
    if (this.isFincaModel(value)) {
      return value;
    }

    const ids = this.getReactiveValueTokens(value);

    return this.lists.fincas.values
      .map((option) => option.raw ?? option.value)
      .find((finca): finca is FincasModel => this.isFincaModel(finca) && ids.includes(String(finca.id ?? ''))) ?? null;
  }

  private getReactiveValueTokens(value: unknown): string[] {
    if (value === null || value === undefined || value === '') {
      return [];
    }

    if (Array.isArray(value)) {
      return value.flatMap((item) => this.getReactiveValueTokens(item));
    }

    if (typeof value === 'object' && 'id' in value) {
      const id = (value as { readonly id?: unknown }).id;

      return id === null || id === undefined ? [] : [String(id)];
    }

    return String(value)
      .split(/[;,]/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  }

  private isFincaModel(value: unknown): value is FincasModel {
    return typeof value === 'object' && value !== null && 'id' in value && 'id_cliente' in value;
  }

  constructor(
    type: FormType,
    _model: Record<string, string>,
    table: (string[] | string[][])[] | null
  ) {
    this.formType = +type;

    this.formFields = {
      name: 'fertirrigacion',
      header: 'Riego',
      type: this.formType,
      sections: this.getFormSections(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
      uploadButton: {
        isEnabled: false,
        label: ''
      },
      signed: false,
      dropdownCatalogs: this.getDropdownCatalogs(),
    };
  }

  private getDropdownCatalogs(): ReactiveDropdownCatalogs {
    return {
      critical: this.formType < 3
        ? ['id_finca', 'ids_sectores', 'ids_parcelas']
        : ['finca', 'sectores_nombres', 'parcelas_nombres'],
      deferred: ['cultivo', 'sistema_riego', 'id_comunidad_regantes', 'id_maquinaria'],
    };
  }
}